// frontend_react/src/context/ReceiptContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

import {
  fetchReceiptFull as apiFetchFull,
  processReceiptPayment as apiProcessPayment,
} from "../services/receiptApi";
import { useCompany } from "./CompanyContext";
import { useAdminScope } from "./AdminScopeContext";

const ReceiptContext = createContext(null);

export function ReceiptProvider({ children }) {
  const {
    user: currentUser,
    companyUuid,
    companyName,
  } = useCompany();
  const authenticatedUser = currentUser;
  const { selectedCompanyUuid } = useAdminScope();

  const [company, setCompany] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [lines, setLines] = useState([]);
  const [payment, setPayment] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearReceipt = useCallback(() => {
    setCompany(null);
    setReceipt(null);
    setLines([]);
    setPayment(null);
    setError(null);
  }, []);

  const fetchFullReceipt = useCallback(
    async (uuid) => {
      if (!uuid || !authenticatedUser) return null;

      setLoading(true);
      setError(null);

      try {
        const data = await apiFetchFull(uuid, {
          companyUuid: selectedCompanyUuid,
        });

        setCompany(data.company ?? null);
        setReceipt(data.receipt ?? null);
        setLines(data.lines ?? []);
        setPayment(data.payment ?? data.payments ?? null);

        return data;
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Erreur lors du chargement du ticket";

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authenticatedUser, selectedCompanyUuid]
  );

  const processPayment = useCallback(
    async (
      uuid,
      means,
      amount = undefined,
      checkFormat = undefined
    ) => {
      if (!uuid) return null;

      setLoading(true);
      setError(null);

      try {
        const { data } = await apiProcessPayment(
          uuid,
          means,
          amount,
          checkFormat,
          {
            companyUuid: selectedCompanyUuid,
          }
        );

        await fetchFullReceipt(uuid);

        return data;
      } catch (err) {
        const message =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Erreur lors du paiement du ticket";

        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchFullReceipt, selectedCompanyUuid]
  );

  const value = useMemo(
    () => ({
      company,
      receipt,
      lines,
      payment,

      loading,
      error,

      // Multi-company frontend
      companyUuid,
      companyName,

      fetchFullReceipt,
      clearReceipt,
      processPayment,

      setError,
    }),
    [
      company,
      receipt,
      lines,
      payment,
      loading,
      error,
      companyUuid,
      companyName,
      fetchFullReceipt,
      clearReceipt,
      processPayment,
    ]
  );

  return (
    <ReceiptContext.Provider value={value}>
      {children}
    </ReceiptContext.Provider>
  );
}

ReceiptProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useReceipt() {
  const ctx = useContext(ReceiptContext);

  if (!ctx) {
    throw new Error("useReceipt must be used within ReceiptProvider");
  }

  return ctx;
}